import { useAuth, useClerk, useUser } from "@clerk/react"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import queryClient from "providers/queryClient"
import React, { useEffect, useRef, useState } from "react"
import { billingApi } from "services/billingApi"
import { clearStoredCheckoutSessionId } from "services/billingCheckoutStorage"
import { identifyPlatformUser, resetPlatformAnalytics } from "services/platformAnalytics"
import { tintVTOService } from "services/tintvto"
import { clearBillingState } from "store/billing/billingSlice"
import { clearProductSlice, setTypes } from "store/product/productSlice"
import { clearUserInfo, selectUserInfo, setUserInfo } from "store/user/userSlice"
import { initializeUser } from "store/user/userThunks"
import { UserStatusTypes } from "types"
import {
  acknowledgeClerkSelfServiceSignupIntent,
  readClerkSelfServiceSignupIntent,
} from "utils/authSessionStorage"
import { buildWorkspaceNameFromEmail, getPrimaryClerkEmail } from "utils/clerkOrganization"
import { logger } from "utils/logger"
import {
  resolveBackendActorResolutionPlan,
  resolveBackendVerifiedUserGroups,
} from "utils/userGroups"
import { normalizeMerchantStatus } from "utils/userStatus"
import { TintMark } from "@/components/TintMark/TintMark"
import { isLocalAdminAuthBypassEnabled, typedEnv } from "@/config/typedEnv"

type ClerkWindow = Window & {
  Clerk?: {
    signOut?: (options?: { redirectUrl?: string }) => Promise<void>
  }
}

type ClerkUserLike = {
  fullName?: string | null
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  primaryEmailAddress?: {
    emailAddress?: string | null
  } | null
  emailAddresses?: Array<{ emailAddress?: string | null }>
  organizationMemberships?: Array<{
    organization?: {
      id?: string | null
    } | null
  }>
} | null

type ClerkOrganizationLike = {
  id?: string | null
}

const AUTH_RESOLUTION_REASON = "auth-resolution"
const CLERK_TOKEN_MAX_ATTEMPTS = 5
const CLERK_TOKEN_RETRY_DELAY_MS = 500
const MERCHANT_RESOLUTION_MAX_ATTEMPTS = 12
const MERCHANT_RESOLUTION_RETRY_DELAY_MS = 3000

// Circuit breaker for the auth-resolution bounce. When sign-out cannot destroy
// the session (Clerk FAPI down, blocked third-party request), the redirect
// lands back on a still-signed-in app that re-runs resolution and bounces
// again — an infinite spinner. The bounce count is persisted across the
// full-page reloads the redirect causes; past the cap we stop redirecting and
// render a terminal, actionable screen instead of looping.
const AUTH_RESOLUTION_BOUNCE_KEY = "tint.authResolutionBounces"
const AUTH_RESOLUTION_MAX_BOUNCES = 3
// clerk.signOut can hang without rejecting (stalled FAPI request); past this
// deadline the redirect happens anyway via window.location.replace.
const AUTH_SIGNOUT_TIMEOUT_MS = 8_000

const readAuthResolutionBounces = (): number => {
  try {
    return Number(window.sessionStorage.getItem(AUTH_RESOLUTION_BOUNCE_KEY)) || 0
  } catch {
    return 0
  }
}

const bumpAuthResolutionBounces = (): number => {
  const next = readAuthResolutionBounces() + 1
  try {
    window.sessionStorage.setItem(AUTH_RESOLUTION_BOUNCE_KEY, String(next))
  } catch {
    // Private mode / storage disabled: the breaker degrades to per-load only.
  }
  return next
}

const clearAuthResolutionBounces = (): void => {
  try {
    window.sessionStorage.removeItem(AUTH_RESOLUTION_BOUNCE_KEY)
  } catch {
    // no-op
  }
}

type ProvisioningState = {
  isVisible: boolean
  progress: number
  stepLabel: string
}

const INITIAL_PROVISIONING_STATE: ProvisioningState = {
  isVisible: false,
  progress: 0,
  stepLabel: "",
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const tokenParts = token.split(".")
    if (tokenParts.length < 2) return null

    const normalizedPayload = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/")
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "=")

    return JSON.parse(window.atob(paddedPayload))
  } catch {
    return null
  }
}

const toPlatformUsername = (sub: string): string => {
  const normalized = sub.startsWith("user_") ? sub.slice(5) : sub
  return normalized.endsWith("-PLATFORM") ? normalized : `${normalized}-PLATFORM`
}

const toAppGroup = (group: string): string => {
  const normalized = group.trim().toLowerCase()
  if (normalized === "admin") return "Admin"
  if (normalized === "merchant") return "Merchant"
  if (normalized === "editor") return "Editor"
  return group.trim()
}

const normalizeGroups = (payload: Record<string, unknown> | null): string[] => {
  const source = payload?.groups ?? payload?.["cognito:groups"]
  const groupsFromClaims = Array.isArray(source)
    ? source.filter((group): group is string => typeof group === "string")
    : typeof source === "string"
    ? [source]
    : []

  const groups = groupsFromClaims
    .map((group) => group.trim())
    .filter(Boolean)
    .map(toAppGroup)

  return [...new Set(groups)]
}

const normalizeDisplayName = (value: unknown): string => {
  if (typeof value !== "string") return ""
  return value.trim()
}

const formatEmailToDisplayName = (email: string): string => {
  const normalizedEmail = email.trim()
  if (!normalizedEmail) return ""

  const [localPart] = normalizedEmail.split("@")
  if (!localPart) return normalizedEmail

  const words = localPart
    .split(/[._-]+/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)

  if (!words.length) return normalizedEmail

  return words.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(" ")
}

const resolveDisplayNameFromClaims = (payload: Record<string, unknown> | null): string => {
  const fullName = normalizeDisplayName(payload?.name)
  if (fullName) return fullName

  const givenName = normalizeDisplayName(payload?.given_name)
  const familyName = normalizeDisplayName(payload?.family_name)
  const combined = [givenName, familyName].filter(Boolean).join(" ").trim()
  if (combined) return combined

  const preferredUsername = normalizeDisplayName(payload?.preferred_username)
  if (preferredUsername) return preferredUsername

  const username = normalizeDisplayName(payload?.username)
  if (username) return username

  const email = normalizeDisplayName(payload?.email)
  if (email) return formatEmailToDisplayName(email)

  return ""
}

const resolveDisplayNameFromClerkUser = (user: ClerkUserLike): string => {
  if (!user) return ""

  const fullName = normalizeDisplayName(user.fullName)
  if (fullName) return fullName

  const givenName = normalizeDisplayName(user.firstName)
  const familyName = normalizeDisplayName(user.lastName)
  const combined = [givenName, familyName].filter(Boolean).join(" ").trim()
  if (combined) return combined

  const username = normalizeDisplayName(user.username)
  if (username) return username

  const primaryEmail = normalizeDisplayName(user.primaryEmailAddress?.emailAddress)
  if (primaryEmail) return formatEmailToDisplayName(primaryEmail)

  const fallbackEmail = normalizeDisplayName(user.emailAddresses?.[0]?.emailAddress)
  if (fallbackEmail) return formatEmailToDisplayName(fallbackEmail)

  return ""
}

const readLocalAdminEnv = (name: keyof typeof typedEnv, fallback: string): string => {
  const value = typedEnv[name]
  return typeof value === "string" && value.trim() ? value.trim() : fallback
}

const buildLocalAdminUserInfo = () => ({
  displayName: readLocalAdminEnv("VITE_LOCAL_ADMIN_NAME", "LocalAdmin"),
  groups: ["Admin"],
  merchantId: readLocalAdminEnv(
    "VITE_LOCAL_PLATFORM_MERCHANT_ID",
    "mch_4f51260e-4a42-419b-8888-6ef44c523fef"
  ),
  status: UserStatusTypes.ACTIVE,
  username: readLocalAdminEnv("VITE_LOCAL_ADMIN_USER_ID", "user_local_admin"),
})

const sanitizeIdentity = (value: string): string => {
  if (!value) return ""
  if (value.length <= 6) return `${value.slice(0, 2)}***`
  return `${value.slice(0, 4)}...${value.slice(-4)}`
}

const getErrorType = (error: unknown): string => {
  if (error instanceof Error) return error.name
  if (typeof error === "object" && error) {
    if ("name" in error && typeof error.name === "string") return error.name
    if ("statusCode" in error && typeof error.statusCode === "number")
      return `http_${error.statusCode}`
  }

  return "unknown_error"
}

const areStringArraysEqual = (first: string[], second: string[]) => {
  if (first.length !== second.length) return false
  return first.every((value, index) => value === second[index])
}

const Initializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch()
  const { isLoaded, isSignedIn } = useAuth()
  const clerk = useClerk()
  const { isLoaded: isClerkUserLoaded, user: clerkUser } = useUser()
  const userInfo = useAppSelector(selectUserInfo)
  const [isInitializing, setIsInitializing] = useState(true)
  const [authResolutionExhausted, setAuthResolutionExhausted] = useState(false)
  const [provisioningState, setProvisioningState] = useState<ProvisioningState>(
    INITIAL_PROVISIONING_STATE
  )
  const selfServiceOrganizationCreationRef = useRef<{
    key: string
    promise: Promise<ClerkOrganizationLike>
  } | null>(null)
  const selfServiceSignupIntentRef = useRef<{
    pending: boolean
    sub: string
  } | null>(null)
  const clerkDisplayName = resolveDisplayNameFromClerkUser(clerkUser ?? null)

  // Clerk's `useUser()`/`useClerk()` return a NEW object reference whenever
  // Clerk re-renders the SAME user (token refresh, setActive, user.reload) —
  // several times a minute. The auth-resolution effect must depend on the
  // stable identity (the user id), NOT on these churning object references, or
  // every internal Clerk re-render cancels and restarts the whole async
  // sequence and it never completes (infinite "loading"). The effect reads the
  // latest Clerk objects through refs so it can use current values without
  // re-subscribing to their reference identity.
  const clerkRef = useRef(clerk)
  const clerkUserRef = useRef(clerkUser)
  const clerkDisplayNameRef = useRef(clerkDisplayName)

  useEffect(() => {
    clerkRef.current = clerk
    clerkUserRef.current = clerkUser
    clerkDisplayNameRef.current = clerkDisplayName
  }, [clerk, clerkDisplayName, clerkUser])

  useEffect(() => {
    void dispatch(initializeUser())
  }, [dispatch])

  useEffect(() => {
    let isCanceled = false

    const resetAuthState = () => {
      selfServiceSignupIntentRef.current = null
      clearStoredCheckoutSessionId()
      resetPlatformAnalytics()
      dispatch(clearBillingState())
      dispatch(clearUserInfo())
      dispatch(clearProductSlice())
      queryClient.clear()
      setProvisioningState(INITIAL_PROVISIONING_STATE)
    }

    const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

    const waitForClerkAppsyncToken = async (): Promise<string | null> => {
      for (let attempt = 1; attempt <= CLERK_TOKEN_MAX_ATTEMPTS; attempt++) {
        if (isCanceled) return null

        try {
          const token = await tintVTOService.getClerkAppsyncToken()
          if (token) return token

          logger.warn("auth", "clerk_token_retry_attempt_failed", {
            attempt,
            errorType: "empty_token",
            maxAttempts: CLERK_TOKEN_MAX_ATTEMPTS,
          })
        } catch (error) {
          logger.warn("auth", "clerk_token_retry_attempt_failed", {
            attempt,
            errorType: getErrorType(error),
            maxAttempts: CLERK_TOKEN_MAX_ATTEMPTS,
          })
        }

        if (attempt < CLERK_TOKEN_MAX_ATTEMPTS) await delay(CLERK_TOKEN_RETRY_DELAY_MS)
      }

      return null
    }

    const blockAuthResolution = async (event: string, context: Record<string, unknown>) => {
      if (isCanceled) return

      logger.error("auth", event, {
        reason: AUTH_RESOLUTION_REASON,
        ...context,
      })

      resetAuthState()

      setIsInitializing(false)

      // Past the bounce cap the redirect is clearly not clearing the session,
      // so stop redirecting (which would reload straight back into this path)
      // and render a terminal screen the user can act on.
      if (bumpAuthResolutionBounces() > AUTH_RESOLUTION_MAX_BOUNCES) {
        logger.error("auth", "merchant_auth_block_bounce_limit", {
          reason: AUTH_RESOLUTION_REASON,
          ...context,
        })
        setAuthResolutionExhausted(true)
        return
      }

      // mode=sign-in: the bounced user HAS an account (they just signed in) —
      // landing them on the default sign-up form invites a duplicate account.
      const redirectPath = `/login?reason=${AUTH_RESOLUTION_REASON}&mode=sign-in`
      const redirectUrl = `${window.location.origin}${redirectPath}`
      const clerk = (window as ClerkWindow).Clerk

      if (!clerk?.signOut) {
        window.location.replace(redirectPath)
        return
      }

      try {
        // signOut normally redirects and never resolves; the race guards the
        // case where the FAPI request hangs without rejecting — otherwise the
        // user is stranded on a dead screen with cleared state.
        await Promise.race([
          clerk.signOut({ redirectUrl }),
          delay(AUTH_SIGNOUT_TIMEOUT_MS).then(() => {
            throw new Error("signout_timeout")
          }),
        ])
        return
      } catch (error) {
        logger.error("auth", "merchant_auth_block_signout_failed", {
          reason: AUTH_RESOLUTION_REASON,
          errorType: getErrorType(error),
        })
      }

      window.location.replace(redirectPath)
    }

    // Resolve the merchant natively from the platform (the mch_ merchant the
    // Clerk webhook provisions), replacing the legacy AppSync resolveMerchantByUName
    // whose customer-sync record is never written for new Clerk merchants. One
    // getCurrentAdmin poll returns id + status atomically. getCurrentAdmin mints a
    // FRESH token per attempt (adminApi.forceFreshToken) so each attempt is a new
    // API Gateway authorizer cache key — it re-invokes the authorizer instead of
    // hitting a cached Deny, resolving as soon as the webhook binds the merchant.
    // All errors are caught + retried to absorb the brief provisioning window.
    type PlatformResolution =
      | {
          kind: "merchant"
          legacyMerchantId?: string
          merchantId: string
          status: UserStatusTypes
        }
      | { kind: "superadmin" }

    const resolveCurrentPlatformActor = async (): Promise<PlatformResolution | null> => {
      const me = await tintVTOService.getCurrentAdmin()
      // Admit admin ONLY on an explicit superadmin role, never on a missing
      // merchant, so a backend DTO regression can't widen privileges.
      if (me.role === "superadmin") return { kind: "superadmin" }
      if (me.merchant?.id && me.merchant.provisioned)
        return {
          kind: "merchant",
          ...(me.merchant.legacyMerchantId
            ? { legacyMerchantId: me.merchant.legacyMerchantId }
            : {}),
          merchantId: me.merchant.id,
          status: normalizeMerchantStatus(me.merchant.status),
        }

      return null
    }

    const waitForPlatformMerchant = async (context: {
      sub: string
      username: string
    }): Promise<PlatformResolution | null> => {
      for (let attempt = 1; attempt <= MERCHANT_RESOLUTION_MAX_ATTEMPTS; attempt++) {
        if (isCanceled) return null

        const progress = Math.min(
          96,
          15 + Math.round((attempt / MERCHANT_RESOLUTION_MAX_ATTEMPTS) * 81)
        )
        setProvisioningState({
          isVisible: true,
          progress,
          stepLabel: `Preparing your workspace (${attempt}/${MERCHANT_RESOLUTION_MAX_ATTEMPTS})`,
        })

        try {
          const resolution = await resolveCurrentPlatformActor()
          if (resolution) return resolution
        } catch (error) {
          logger.warn("auth", "platform_merchant_resolve_retry_attempt_failed", {
            ...context,
            errorType: getErrorType(error),
            attempt,
            maxAttempts: MERCHANT_RESOLUTION_MAX_ATTEMPTS,
          })
        }

        if (attempt < MERCHANT_RESOLUTION_MAX_ATTEMPTS)
          await delay(MERCHANT_RESOLUTION_RETRY_DELAY_MS)
      }

      return null
    }

    const ensureSelfServiceOrganization = async (context: { sub: string; username: string }) => {
      const existingOrganizationId =
        clerkUserRef.current?.organizationMemberships?.find(
          (membership) => membership.organization?.id
        )?.organization?.id ??
        clerkRef.current.organization?.id ??
        ""

      if (existingOrganizationId) {
        if (!clerkRef.current.organization?.id)
          await clerkRef.current.setActive({ organization: existingOrganizationId })
        return true
      }

      const emailAddress = getPrimaryClerkEmail(clerkUserRef.current)
      if (!emailAddress) {
        await blockAuthResolution("self_service_organization_missing_email", context)
        return false
      }

      setProvisioningState({
        isVisible: true,
        progress: 10,
        stepLabel: "Preparing your workspace",
      })

      const creationKey = context.sub || context.username || emailAddress
      let organizationCreation = selfServiceOrganizationCreationRef.current

      if (organizationCreation?.key !== creationKey) {
        const promise = clerkRef.current
          .createOrganization({
            name: buildWorkspaceNameFromEmail(emailAddress),
          })
          .catch((error) => {
            if (selfServiceOrganizationCreationRef.current?.key === creationKey)
              selfServiceOrganizationCreationRef.current = null

            throw error
          })

        organizationCreation = {
          key: creationKey,
          promise,
        }
        selfServiceOrganizationCreationRef.current = organizationCreation
      }

      try {
        const organization = await organizationCreation.promise
        if (isCanceled) return false

        const organizationId = organization.id?.trim()
        if (!organizationId) {
          await blockAuthResolution("self_service_organization_missing_id", context)
          return false
        }

        await clerkRef.current.setActive({ organization: organizationId })
        await clerkRef.current.user?.reload?.()
        return true
      } catch (error) {
        await blockAuthResolution("self_service_organization_create_failed", {
          ...context,
          errorType: getErrorType(error),
        })
        return false
      }
    }

    const syncClerkAuth = async () => {
      if (isLocalAdminAuthBypassEnabled) {
        const nextUserInfo = buildLocalAdminUserInfo()
        const isSameUserInfo =
          userInfo.username === nextUserInfo.username &&
          userInfo.displayName === nextUserInfo.displayName &&
          userInfo.legacyMerchantId === undefined &&
          userInfo.merchantId === nextUserInfo.merchantId &&
          userInfo.status === nextUserInfo.status &&
          areStringArraysEqual(userInfo.groups, nextUserInfo.groups)

        if (!isSameUserInfo) dispatch(setUserInfo({ userInfo: nextUserInfo }))
        identifyPlatformUser(nextUserInfo)
        setProvisioningState(INITIAL_PROVISIONING_STATE)
        setIsInitializing(false)
        return
      }

      if (!isLoaded) {
        setIsInitializing(true)
        return
      }

      if (isSignedIn && !isClerkUserLoaded) {
        setIsInitializing(true)
        return
      }

      if (!isSignedIn) {
        selfServiceSignupIntentRef.current = null
        if (userInfo.username) resetAuthState()

        // A clean signed-out state means the bounce loop was escaped; reset it
        // so the next sign-in starts fresh.
        clearAuthResolutionBounces()
        setAuthResolutionExhausted(false)
        setProvisioningState(INITIAL_PROVISIONING_STATE)
        setIsInitializing(false)
        return
      }

      const hasResolvedCommonUser = Boolean(userInfo.username)

      if (!hasResolvedCommonUser) setIsInitializing(true)

      const token = await waitForClerkAppsyncToken()
      if (isCanceled) return

      const payload = token ? decodeJwtPayload(token) : null
      const rawSub = typeof payload?.sub === "string" ? payload.sub : ""
      if (!rawSub) {
        await blockAuthResolution("jwt_template_missing_sub", {
          claimKeys: payload ? Object.keys(payload) : [],
        })
        return
      }

      const username = toPlatformUsername(rawSub)
      const hasResolvedCurrentClerkUser = userInfo.username === username

      if (!hasResolvedCurrentClerkUser) setIsInitializing(true)

      const claimedGroups = normalizeGroups(payload)
      const displayName =
        clerkDisplayNameRef.current || resolveDisplayNameFromClaims(payload) || username

      // Resolve the platform actor once and reuse the result below. A raw
      // Clerk Admin group is only an organization-role hint; it never grants
      // platform admin access without an explicit backend superadmin actor.
      let resolution: PlatformResolution | null = null
      try {
        resolution = await resolveCurrentPlatformActor()
      } catch (error) {
        logger.warn("auth", "platform_actor_initial_resolve_failed", {
          errorType: getErrorType(error),
          sub: sanitizeIdentity(rawSub),
        })
      }
      if (isCanceled) return

      const completedSignupAttemptId = readClerkSelfServiceSignupIntent(rawSub)
      if (completedSignupAttemptId)
        selfServiceSignupIntentRef.current = {
          pending: true,
          sub: rawSub,
        }
      else if (selfServiceSignupIntentRef.current?.sub !== rawSub)
        selfServiceSignupIntentRef.current = {
          pending: false,
          sub: rawSub,
        }
      const hasSelfServiceSignupIntent = Boolean(selfServiceSignupIntentRef.current.pending)
      const actorResolutionPlan = resolveBackendActorResolutionPlan(
        resolution?.kind ?? null,
        hasSelfServiceSignupIntent
      )
      if (resolution) {
        acknowledgeClerkSelfServiceSignupIntent(rawSub)
        selfServiceSignupIntentRef.current.pending = false
      }

      const verifiedGroups = resolveBackendVerifiedUserGroups(
        claimedGroups,
        resolution?.kind ?? null
      )
      let groups = verifiedGroups.groups
      const hasUnverifiedAdminClaim = verifiedGroups.hasUnverifiedAdminClaim
      let isAdmin = verifiedGroups.isAdmin

      if (!isAdmin && (hasUnverifiedAdminClaim || groups.length === 0))
        logger.warn(
          "auth",
          hasUnverifiedAdminClaim
            ? "jwt_admin_group_unverified_merchant_fallback"
            : "jwt_template_missing_groups_merchant_fallback",
          {
            claimKeys: payload ? Object.keys(payload) : [],
            sub: sanitizeIdentity(rawSub),
            aud: payload?.aud,
            iss: payload?.iss,
          }
        )

      let merchantId = ""
      let legacyMerchantId: string | undefined
      let status = UserStatusTypes.ACTIVE
      if (!isAdmin) {
        const sanitizedContext = {
          sub: sanitizeIdentity(rawSub),
          username: sanitizeIdentity(username),
        }

        if (!resolution) {
          // Only an explicit sign-up flow may create/activate a Clerk
          // organization. A sign-in with an unavailable backend remains
          // read-only and fails closed, regardless of its JWT group claims.
          if (
            actorResolutionPlan.shouldProvisionSelfServiceOrganization &&
            !(await ensureSelfServiceOrganization(sanitizedContext))
          )
            return
          if (isCanceled) return

          if (actorResolutionPlan.shouldRetryActorResolution)
            resolution = await waitForPlatformMerchant(sanitizedContext)
          if (isCanceled) return
        }

        if (resolution) {
          acknowledgeClerkSelfServiceSignupIntent(rawSub)
          selfServiceSignupIntentRef.current.pending = false
        }

        if (resolution?.kind === "merchant") {
          merchantId = resolution.merchantId
          legacyMerchantId = resolution.legacyMerchantId
          status = resolution.status
        } else if (resolution?.kind === "superadmin") {
          if (isCanceled) return

          groups = resolveBackendVerifiedUserGroups(claimedGroups, "superadmin").groups
          isAdmin = true
        } else {
          await blockAuthResolution("merchant_resolve_via_platform_failed", {
            ...sanitizedContext,
            errorType: "not_resolved_after_retries",
          })
          return
        }
      }

      if (isCanceled) return

      const nextUserInfo = {
        username,
        displayName,
        groups,
        ...(legacyMerchantId ? { legacyMerchantId } : {}),
        merchantId,
        status,
      }

      const isSameUserInfo =
        userInfo.username === nextUserInfo.username &&
        userInfo.displayName === nextUserInfo.displayName &&
        userInfo.legacyMerchantId === nextUserInfo.legacyMerchantId &&
        userInfo.merchantId === nextUserInfo.merchantId &&
        userInfo.status === nextUserInfo.status &&
        areStringArraysEqual(userInfo.groups, nextUserInfo.groups)

      if (!isSameUserInfo) dispatch(setUserInfo({ userInfo: nextUserInfo }))
      identifyPlatformUser(nextUserInfo)

      if (!isAdmin && status === UserStatusTypes.ACTIVE)
        try {
          await billingApi.markOnboardingActivation({
            suppressPaywall: true,
          })
        } catch (error) {
          logger.warn("billing", "onboarding_activation_failed", {
            errorType: getErrorType(error),
            merchantId: sanitizeIdentity(merchantId),
          })
        }

      if (isCanceled) return

      // Resolution succeeded — reset the bounce breaker so a future genuine
      // failure gets its full allowance again.
      clearAuthResolutionBounces()

      setProvisioningState({
        isVisible: false,
        progress: 100,
        stepLabel: "",
      })
      setIsInitializing(false)
    }

    void syncClerkAuth()

    return () => {
      isCanceled = true
    }
  }, [
    dispatch,
    // Depend on the STABLE Clerk identity, not the churning clerk/clerkUser
    // object refs (read via refs inside the effect) — otherwise every internal
    // Clerk re-render restarts the async auth resolution and it never finishes.
    clerkUser?.id,
    isClerkUserLoaded,
    isLoaded,
    isSignedIn,
    userInfo.displayName,
    userInfo.groups,
    userInfo.legacyMerchantId,
    userInfo.merchantId,
    userInfo.status,
    userInfo.username,
  ])

  useEffect(() => {
    if (isInitializing || !userInfo.username) return

    identifyPlatformUser(userInfo)
  }, [isInitializing, userInfo])

  useEffect(() => {
    if (isInitializing || !userInfo.username) return

    let isCanceled = false

    const loadTypes = async () => {
      try {
        const { data } = await tintVTOService.types()
        if (isCanceled) return

        dispatch(setTypes(data?.types ?? []))
      } catch (error) {
        if (isCanceled) return

        console.warn("Failed to preload product types", error)
        dispatch(setTypes([]))
      }
    }

    void loadTypes()

    return () => {
      isCanceled = true
    }
  }, [dispatch, isInitializing, userInfo.username])

  const isResolvingCommonClerkUser =
    Boolean(isSignedIn) && (!isClerkUserLoaded || !userInfo.username)

  // Terminal state: the session could not be resolved and could not be cleared
  // by repeated sign-out. Rather than spin forever, give the user an actionable
  // screen with a manual sign-out that reloads to a clean state.
  if (authResolutionExhausted)
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <TintMark className="size-12 rounded-xl" />
        <p className="max-w-md text-sm font-medium leading-relaxed text-muted-foreground">
          We couldn&rsquo;t finish signing you in — your workspace isn&rsquo;t linked to this
          account yet. Please sign out and try again, or contact support if this keeps happening.
        </p>
        <button
          type="button"
          className="h-11 rounded-xl bg-[var(--auth-primary,#6d3bf5)] px-5 text-sm font-bold text-white"
          data-testid="auth-resolution-exhausted-signout"
          onClick={() => {
            clearAuthResolutionBounces()
            void clerk
              .signOut()
              .catch(() => undefined)
              .finally(() => window.location.replace("/login?mode=sign-in"))
          }}
        >
          Sign out
        </button>
      </div>
    )

  if (isInitializing || isResolvingCommonClerkUser)
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background">
        <TintMark className="size-12 animate-pulse rounded-xl" />
        {provisioningState.isVisible && (
          <div
            className="flex w-64 flex-col items-center gap-2"
            data-testid="provisioning-progress"
          >
            <p className="text-sm font-medium text-muted-foreground">
              {provisioningState.stepLabel}
            </p>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-[var(--auth-primary,#6d3bf5)] transition-[width] duration-500"
                style={{ width: `${provisioningState.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    )

  return <>{children}</>
}

export default Initializer
